/**
 * The blue banner at the top of a dashboard: who you are and where to start,
 * with the page's main actions on it. It stands in for the page's ordinary
 * header, so the title inside it is the page's h1.
 */
export const WelcomeBanner = ({ eyebrow, title, text, children }) => (
  <section className="welcome-banner">
    <div className="welcome-copy">
      {eyebrow ? <p className="welcome-eyebrow">{eyebrow}</p> : null}
      <h1>{title}</h1>
      <span className="welcome-rule" aria-hidden="true" />
      {text ? <p className="welcome-text">{text}</p> : null}
      {children ? <div className="welcome-actions">{children}</div> : null}
    </div>
    <BannerArt />
  </section>
);

/** A bill, a meter, a tap and a calendar: the month's housing costs, drawn. */
const BannerArt = () => (
  <svg className="welcome-art" viewBox="0 0 420 260" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="wb-orange" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ffa54a" />
        <stop offset="1" stopColor="#f2711c" />
      </linearGradient>
      <linearGradient id="wb-metal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#eef2f7" />
        <stop offset="1" stopColor="#a9b4c4" />
      </linearGradient>
    </defs>

    {/* soft rings behind */}
    <circle cx="250" cy="120" r="118" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
    <circle cx="250" cy="120" r="88" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2" strokeDasharray="3 7" />

    {/* bolt badge, left */}
    <circle cx="58" cy="78" r="34" fill="#163a7d" stroke="#3d8bff" strokeWidth="3" />
    <path d="M62 56 46 82h13l-3 20 17-27H60z" fill="#ffb23f" />

    {/* the bill */}
    <g transform="rotate(-4 170 130)">
      <rect x="112" y="36" width="122" height="168" rx="12" fill="#fff" />
      <path d="M128 64l11-9 11 9v14h-22z" fill="#2f7be5" />
      <rect x="157" y="58" width="58" height="7" rx="3.5" fill="#d6e0ee" />
      <rect x="157" y="72" width="40" height="7" rx="3.5" fill="#d6e0ee" />
      <rect x="128" y="96" width="90" height="7" rx="3.5" fill="#b9c8de" />
      <rect x="128" y="112" width="70" height="7" rx="3.5" fill="#d6e0ee" />
      <rect x="128" y="128" width="84" height="7" rx="3.5" fill="#b9c8de" />
      <rect x="128" y="144" width="56" height="7" rx="3.5" fill="#d6e0ee" />
      <rect x="128" y="168" width="90" height="7" rx="3.5" fill="#d6e0ee" />
    </g>

    {/* orange bolt tile, front left */}
    <rect x="86" y="150" width="78" height="78" rx="14" fill="url(#wb-orange)" />
    <path d="M131 162 108 194h16l-5 24 25-35h-16z" fill="#fff" />

    {/* the tap and a drop */}
    <path d="M196 132h44a14 14 0 0 1 14 14v16" fill="none" stroke="url(#wb-metal)" strokeWidth="16" strokeLinecap="round" />
    <rect x="206" y="112" width="12" height="20" rx="3" fill="#c5cfdc" />
    <rect x="196" y="106" width="32" height="9" rx="4.5" fill="#e6ebf2" />
    <path d="M254 186c-7 10-10 15-10 20a10 10 0 0 0 20 0c0-5-3-10-10-20z" fill="#3aa0ff" />

    {/* the meter */}
    <circle cx="352" cy="74" r="56" fill="#fff" />
    <path d="M308 86a45 45 0 0 1 88 0" fill="none" stroke="#e9eef5" strokeWidth="12" />
    <path d="M308 86a45 45 0 0 1 28-41" fill="none" stroke="#f6c343" strokeWidth="12" />
    <path d="M336 45a45 45 0 0 1 32 0" fill="none" stroke="#f59a2c" strokeWidth="12" />
    <path d="M368 45a45 45 0 0 1 28 41" fill="none" stroke="#e5484d" strokeWidth="12" />
    <path d="M352 82 327 58" stroke="#13254a" strokeWidth="5" strokeLinecap="round" />
    <circle cx="352" cy="82" r="8" fill="#13254a" />

    {/* bolt badge, top right of the bill */}
    <circle cx="276" cy="34" r="24" fill="#163a7d" stroke="#3d8bff" strokeWidth="3" />
    <path d="M279 19 268 37h9l-2 13 12-18h-9z" fill="#fff" />

    {/* house tile on the meter */}
    <rect x="374" y="112" width="40" height="40" rx="8" fill="url(#wb-orange)" />
    <path d="M384 132l10-9 10 9v11h-20z" fill="#fff" />

    {/* the calendar */}
    <rect x="300" y="150" width="104" height="96" rx="12" fill="#fff" />
    <rect x="300" y="150" width="104" height="26" rx="12" fill="#2f7be5" />
    <rect x="300" y="164" width="104" height="12" fill="#2f7be5" />
    {[318, 342, 366, 390].map((x) => (
      <rect key={x} x={x - 3} y="142" width="6" height="18" rx="3" fill="#c5cfdc" />
    ))}
    {[0, 1, 2].map((row) =>
      [0, 1, 2, 3].map((col) => (
        <rect
          key={`${row}-${col}`}
          x={312 + col * 22}
          y={186 + row * 18}
          width="14"
          height="11"
          rx="3"
          fill={row === 1 && col === 2 ? '#f47b20' : '#d6e0ee'}
        />
      ))
    )}
  </svg>
);
